import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import '../booking/ServicePage.css';

const DURATIONS = [
  { id: '4', icon: '⏱', label: '4 Hours', price: 1200 },
  { id: '8', icon: '🕗', label: '8 Hours', price: 2200 },
  { id: '12', icon: '🌙', label: '12 Hours', price: 3000 },
  { id: '24', icon: '📅', label: 'Full Day', price: 4500 },
];

const VEHICLES = [
  { id: 'sedan', label: 'Sedan', seats: 4, price8: 2200 },
  { id: 'suv', label: 'SUV', seats: 6, price8: 3200 },
];

const RentalForm = () => {
  const [city, setCity] = useState('');
  const [pickupAddr, setPickupAddr] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [duration, setDuration] = useState('8');
  const [vehicleType, setVehicleType] = useState('sedan');
  const [phone, setPhone] = useState('');

  const selectedDuration = DURATIONS.find((d) => d.id === duration);
  const selectedVehicle = VEHICLES.find((v) => v.id === vehicleType);

  const vehiclePrice = useMemo(() => {
    if (!selectedVehicle || !selectedDuration) return 0;
    return Math.round(selectedVehicle.price8 * (selectedDuration.price / 2200));
  }, [selectedVehicle, selectedDuration]);

  const dateTimeLabel = pickupDate
    ? `${pickupDate} ${pickupTime || ''}`.trim()
    : pickupTime || '—';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!city || !pickupDate || !pickupTime || !phone) {
      toast.error('Please fill all required fields');
      return;
    }
    toast.success('Rental request submitted!');
  };

  return (
    <div className="page-body">
      {/* ── FORM SIDE ── */}
      <form className="form-side" onSubmit={handleSubmit}>
        <div className="page-title-row">
          <div className="page-badge badge-bl">Hourly Rental</div>
          <h2 className="page-title">Book by the hour</h2>
          <p className="page-sub">Driver stays with you. Multi-stop. Meetings, errands, shopping — your schedule.</p>
        </div>

        <div className="form-grid">
          <div className="fg span2">
            <label>City</label>
            <input
              type="text"
              placeholder="e.g. Patna"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </div>
          <div className="fg span2">
            <label>Pickup Address</label>
            <input
              type="text"
              placeholder="House no, street, landmark"
              value={pickupAddr}
              onChange={(e) => setPickupAddr(e.target.value)}
            />
          </div>
          <div className="fg">
            <label>Pickup Date</label>
            <input
              type="date"
              value={pickupDate}
              onChange={(e) => setPickupDate(e.target.value)}
              required
            />
          </div>
          <div className="fg">
            <label>Pickup Time</label>
            <input
              type="time"
              value={pickupTime}
              onChange={(e) => setPickupTime(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 20 }}>
          <label>Duration</label>
          <div className="radio-cards cards-4">
            {DURATIONS.map((d) => (
              <div
                key={d.id}
                className={`rc${duration === d.id ? ' active' : ''}`}
                onClick={() => setDuration(d.id)}
              >
                <div className="rc-icon">{d.icon}</div>
                <div className="rc-label">{d.label}</div>
                <div className="rc-sub">₹{d.price.toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 20 }}>
          <label>Vehicle Type</label>
          <div className="fleet-grid">
            {VEHICLES.map((v) => {
              const priceForDuration = selectedDuration
                ? Math.round(v.price8 * (selectedDuration.price / 2200))
                : v.price8;
              return (
                <div
                  key={v.id}
                  className={`fleet-card${vehicleType === v.id ? ' active' : ''}`}
                  onClick={() => setVehicleType(v.id)}
                >
                  <h5>{v.label}</h5>
                  <div className="fleet-specs">
                    <span className="fleet-spec">👤 {v.seats} seats</span>
                    <span className="fleet-spec">❄️ AC</span>
                  </div>
                  <span className="fleet-price">
                    ₹{priceForDuration.toLocaleString('en-IN')}
                    <span className="fleet-per"> / {selectedDuration?.label || '8 Hours'}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="form-grid">
          <div className="fg span2">
            <label>Mobile Number</label>
            <div className="prefix-wrap">
              <span className="prefix">+91</span>
              <input
                type="tel"
                placeholder="98000 00000"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                maxLength={10}
                required
              />
            </div>
          </div>
        </div>

        <div className="submit-area">
          <div className="price-strip">
            <div className="price-strip-left">
              <div className="plabel">{selectedVehicle?.label} · {selectedDuration?.label} rental</div>
              <div className="pnote">Includes driver &amp; AC</div>
            </div>
            <div className="pamount">₹{vehiclePrice.toLocaleString('en-IN')}</div>
          </div>

          <div className="trust-badges">
            <span className="tb"><span className="tb-dot" />Dedicated driver</span>
            <span className="tb"><span className="tb-dot" />AC included</span>
            <span className="tb"><span className="tb-dot" />Multi-stop</span>
            <span className="tb"><span className="tb-dot" />Up to 80 km included</span>
          </div>

          <button type="submit" className="submit-btn">
            Book rental →
          </button>
        </div>
      </form>

      {/* ── SUMMARY SIDE ── */}
      <div className="summary-side">
        <div className="summary-card">
          <h4>Booking summary</h4>
          <div className="sum-row">
            <span className="sk">Service</span>
            <span className="sv">Hourly Rental ({selectedVehicle?.label || '—'})</span>
          </div>
          <div className="sum-row">
            <span className="sk">City</span>
            <span className="sv">{city || '—'}</span>
          </div>
          <div className="sum-row">
            <span className="sk">Pickup address</span>
            <span className="sv">{pickupAddr || '—'}</span>
          </div>
          <div className="sum-row">
            <span className="sk">Date &amp; time</span>
            <span className="sv">{dateTimeLabel}</span>
          </div>
          <div className="sum-row row-inline">
            <span className="sk">Duration</span>
            <span className="sv">{selectedDuration?.label || '—'}</span>
          </div>
          <div className="sum-row row-inline">
            <span className="sk">Included km</span>
            <span className="sv">80 km</span>
          </div>

          <div className="sum-fare-label">Estimated fare</div>
          <div className="sum-total">
            <span className="sta">₹{vehiclePrice.toLocaleString('en-IN')}</span>
            <span className="stl">Rental fare</span>
          </div>
        </div>

        <div className="fare-breakdown">
          <h4>Fare breakdown:</h4>
          <div className="fb-row">
            <span className="fk">{selectedVehicle?.label} · {selectedDuration?.label}</span>
            <span className="fv">₹{vehiclePrice.toLocaleString('en-IN')}</span>
          </div>
          <div className="fb-row">
            <span className="fk">Driver &amp; AC</span>
            <span className="fv" style={{ color: 'var(--green, #1A7A4A)' }}>Included</span>
          </div>
          <div className="fb-row">
            <span className="fk">Up to 80 km</span>
            <span className="fv" style={{ color: 'var(--green, #1A7A4A)' }}>Included</span>
          </div>
          <div className="fb-row">
            <span className="fk">Extra km</span>
            <span className="fv">₹15/km</span>
          </div>
          <div className="fb-row">
            <span className="fk">Extra hour</span>
            <span className="fv">₹200/hr</span>
          </div>
          <div className="fb-row">
            <span className="fk">Toll / parking</span>
            <span className="fv">At actuals</span>
          </div>
        </div>

        <div className="steps-card">
          <h4>What happens next?</h4>
          <div className="step-item">
            <span className="step-num">1</span>
            <div className="step-text">
              <h6>Book the rental</h6>
              <p>Pick duration, vehicle and confirm.</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-num">2</span>
            <div className="step-text">
              <h6>Driver assigned in 15 mins</h6>
              <p>You receive driver details on WhatsApp.</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-num">3</span>
            <div className="step-text">
              <h6>Ride your schedule</h6>
              <p>Multi-stop, no km worry, driver waits with you.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalForm;
